import React from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ShoppingBag, Trash2, Plus, Minus } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { StockBadge } from './StockBadge';
export function CartDrawer() {
  const {
    items,
    isOpen,
    closeCart,
    removeFromCart,
    updateQuantity,
    total,
    itemCount
  } = useCart();
  return <AnimatePresence data-id="element-51">
      {isOpen && <>
          {/* Backdrop */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={closeCart} className="fixed inset-0 bg-black/50 z-[60] backdrop-blur-sm" data-id="element-52" />

          {/* Drawer */}
          <motion.aside initial={{
        x: '100%'
      }} animate={{
        x: 0
      }} exit={{
        x: '100%'
      }} transition={{
        type: 'spring',
        damping: 30,
        stiffness: 300
      }} className="fixed top-0 right-0 h-full w-full sm:w-[440px] bg-white z-[70] shadow-2xl flex flex-col" data-id="element-53">
            {/* Header */}
            <header className="flex items-center justify-between p-5 border-b border-gray-200" data-id="element-54">
              <div className="flex items-center gap-3" data-id="element-55">
                <ShoppingBag className="w-5 h-5 text-yarbo-dark" data-id="element-56" />
                <h2 className="text-lg font-bold text-yarbo-dark" data-id="element-57">
                  Your Cart{' '}
                  {itemCount > 0 && <span className="text-gray-400 font-normal" data-id="element-58">
                      ({itemCount})
                    </span>}
                </h2>
              </div>
              <button onClick={closeCart} className="p-2 hover:bg-gray-100 rounded-lg text-gray-500" aria-label="Close cart" data-id="element-59">
                <X className="w-5 h-5" data-id="element-60" />
              </button>
            </header>

            {/* Items */}
            <div className="flex-1 overflow-y-auto p-5" data-id="element-61">
              {items.length === 0 ? <div className="text-center py-16" data-id="element-62">
                  <ShoppingBag className="w-12 h-12 text-gray-300 mx-auto mb-4" data-id="element-63" />
                  <h3 className="text-lg font-bold text-yarbo-dark mb-2" data-id="element-64">
                    Your cart is empty
                  </h3>
                  <p className="text-sm text-gray-500 mb-6" data-id="element-65">
                    Add Yarbo products to get started.
                  </p>
                  <Link to="/shop" onClick={closeCart} className="inline-block bg-yarbo-dark text-white font-bold text-sm px-6 py-3 rounded-lg hover:bg-black transition-colors" data-id="element-66">
                    Shop All Products
                  </Link>
                </div> : <ul className="space-y-4" data-id="element-67">
                  {items.map(({
              product,
              quantity
            }) => <li key={product.id} className="flex gap-4 pb-4 border-b border-gray-100 last:border-0" data-id="element-68">
                      <Link to={`/product/${product.slug}`} onClick={closeCart} className="w-20 h-20 bg-gray-50 rounded-lg overflow-hidden flex-shrink-0 p-2" data-id="element-69">
                        <img src={product.image} alt={product.name} className="w-full h-full object-contain" data-id="element-70" />
                      </Link>
                      <div className="flex-1 min-w-0" data-id="element-71">
                        <Link to={`/product/${product.slug}`} onClick={closeCart} className="font-semibold text-sm text-yarbo-dark line-clamp-2 hover:text-yarbo-yellow" data-id="element-72">
                          {product.name}
                        </Link>
                        <div className="flex items-center gap-2 mt-1" data-id="element-73">
                          <StockBadge status={product.status} data-id="element-74" />
                        </div>
                        <div className="flex items-center justify-between mt-3" data-id="element-75">
                          <div className="flex items-center border border-gray-200 rounded-lg" data-id="element-76">
                            <button onClick={() => updateQuantity(product.id, quantity - 1)} className="p-1.5 hover:bg-gray-50 text-gray-600" aria-label="Decrease quantity" data-id="element-77">
                              <Minus className="w-3 h-3" data-id="element-78" />
                            </button>
                            <span className="px-3 text-sm font-semibold w-8 text-center" data-id="element-79">
                              {quantity}
                            </span>
                            <button onClick={() => updateQuantity(product.id, quantity + 1)} className="p-1.5 hover:bg-gray-50 text-gray-600" aria-label="Increase quantity" data-id="element-80">
                              <Plus className="w-3 h-3" data-id="element-81" />
                            </button>
                          </div>
                          <span className="font-bold text-yarbo-dark" data-id="element-82">
                            ${(product.price * quantity).toLocaleString()}
                          </span>
                        </div>
                      </div>
                      <button onClick={() => removeFromCart(product.id)} className="text-gray-400 hover:text-red-500 p-1 self-start" aria-label="Remove from cart" data-id="element-83">
                        <Trash2 className="w-4 h-4" data-id="element-84" />
                      </button>
                    </li>)}
                </ul>}
            </div>

            {/* Footer */}
            {items.length > 0 && <footer className="border-t border-gray-200 p-5 space-y-4 bg-gray-50" data-id="element-85">
                <div className="flex justify-between items-center" data-id="element-86">
                  <span className="text-sm text-gray-600" data-id="element-87">Estimated Total</span>
                  <span className="text-2xl font-extrabold text-yarbo-dark" data-id="element-88">
                    ${total.toLocaleString()}
                  </span>
                </div>
                <p className="text-xs text-gray-500" data-id="element-89">
                  Shipping and taxes calculated at checkout. All items in stock
                  and ready to ship.
                </p>
                <Link to="/checkout" onClick={closeCart} className="block w-full bg-yarbo-dark text-white font-bold text-center py-4 rounded-lg hover:bg-black transition-colors" data-id="element-90">
                  Checkout
                </Link>
                <Link to="/cart" onClick={closeCart} className="block w-full bg-white text-yarbo-dark border-2 border-gray-200 hover:border-yarbo-dark font-bold text-center py-3 rounded-lg transition-colors text-sm" data-id="element-91">
                  View Cart
                </Link>
                <button onClick={closeCart} className="block w-full text-center text-sm font-semibold text-gray-700 hover:text-yarbo-dark" data-id="element-92">
                  Continue Shopping
                </button>
              </footer>}
          </motion.aside>
        </>}
    </AnimatePresence>;
}